import { Interval } from 'luxon';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { getSales } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { InfiniteTable } from 'modules/experience-core';

import ErrorPage from '../components/ErrorPage';
import LoadingPage from '../components/LoadingPage';
import ImageCoverageSection from '../components/image-coverage/ImageCoverageSection';
import MenuTable, { MenuTableProps } from '../components/tables/menu/MenuTable';
import { MenuTableSortableColumnIDs } from '../components/tables/menu/types';
import { useTotalSalesInsight } from '../hooks/useInsightApi';
import useMenuInsights, { DEFAULT_MENU_SORT_STATE } from '../hooks/useMenuInsights';
import { useTelemetryBusinessInsightsV2MenuView } from '../telemetry';
import { InsightEntity } from '../types';

type MenuPageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

const Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 24px;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const MenuPage = ({ insightEntity, dateInterval, isVATEnabled }: MenuPageProps) => {
  const entityId = insightEntity.id;
  useTelemetryBusinessInsightsV2MenuView(insightEntity.type);

  const [sort, setSort] = useState<InfiniteTable.SortState<MenuTableSortableColumnIDs> | undefined>(
    DEFAULT_MENU_SORT_STATE,
  );

  const menuInsights = useMenuInsights({
    entityType: insightEntity.type,
    entityId,
    interval: dateInterval,
    sortBy: sort?.sortBy,
    sortOrder: sort?.sortOrder,
  });
  const {
    data: menuItemsData,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = menuInsights;

  const totalSalesInsight = useTotalSalesInsight(insightEntity, dateInterval);
  const { data: totalSalesData } = totalSalesInsight;

  const menuTableProps: MenuTableProps = useMemo(
    () => ({
      isFetchingNextPage,
      isFetchingPreviousPage,
      totalSales: getSales(totalSalesData?.data.sales?.total, isVATEnabled) ?? 0,
      totalUnits: totalSalesData?.data.sales?.total.units ?? 0,
      items:
        menuItemsData?.pages.flatMap(page => {
          return page.data.menuItems.total.map(item => ({ ...item, id: item.itemName, currency: page?.currency }));
        }) ?? [],
      sort,
      handleSort: (sort: InfiniteTable.SortState<MenuTableSortableColumnIDs>) => setSort(sort),
      onFetchNextPage: hasNextPage ? fetchNextPage : undefined,
      onFetchPreviousPage: hasPreviousPage ? fetchPreviousPage : undefined,
      isVATEnabled,
    }),
    [
      fetchNextPage,
      fetchPreviousPage,
      hasNextPage,
      hasPreviousPage,
      isFetchingNextPage,
      isFetchingPreviousPage,
      isVATEnabled,
      menuItemsData?.pages,
      sort,
      totalSalesData?.data.sales?.total,
    ],
  );

  const allInsightResponses = [menuInsights, totalSalesInsight];

  if (allInsightResponses.some(insight => insight.isFetching && (insight.isLoading || insight.isError))) {
    return <LoadingPage />;
  } else if (allInsightResponses.some(insight => insight.isError || (insight.isLoading && !insight.isFetching))) {
    return (
      <ErrorPage
        onRetry={() => {
          allInsightResponses.forEach(insight => insight.isError && insight.refetch());
        }}
      />
    );
  }

  return (
    <Root>
      <ImageCoverageSection insightEntity={insightEntity} />
      <MenuTable {...menuTableProps} />
    </Root>
  );
};

export default MenuPage;
